import { useState, useEffect } from 'react';
import { X, Link2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SOURCE_SYSTEMS = [
  'SharePoint',
  'OneDrive', 
  'Google Drive',
  'Microsoft 365',
  'Addepar',
  'InfoTrack',
  'Praemium',
  'Xplan',
  'Class Super',
  'BGL',
  'Other'
];

const CATEGORIES = [
  'Legal',
  'Investment',
  'Compliance',
  'Corporate',
  'Estate',
  'Banking',
  'Insurance',
  'Tax',
  'Governance',
  'Other'
];

const REVIEW_CADENCES = [
  'Monthly',
  'Quarterly',
  'Annually',
  'Bi-annually',
  'As needed'
];

const STATUSES = [
  'Verified',
  'Needs review',
  'Pending'
];

export default function LinkModal({ link, entities, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: '',
    sourceSystem: '',
    category: '',
    owner: '',
    reviewCadence: 'Annually',
    status: 'Verified',
    lastVerified: new Date().toISOString().split('T')[0],
    linkedEntityId: '',
    reason: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (link) {
      setFormData({
        title: link.title || '',
        sourceSystem: link.sourceSystem || '',
        category: link.category || '',
        owner: link.owner || '',
        reviewCadence: link.reviewCadence || 'Annually',
        status: link.status || 'Verified',
        lastVerified: link.lastVerified || new Date().toISOString().split('T')[0],
        linkedEntityId: link.entityId || '',
        reason: link.reason || ''
      });
    }
  }, [link]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.sourceSystem) newErrors.sourceSystem = 'Source system is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.owner.trim()) newErrors.owner = 'Owner is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave({
        ...formData,
        entityId: formData.linkedEntityId || null
      });
    }
  };

  const isEditing = !!link;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in" 
      data-testid="link-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="link-modal-title"
      aria-describedby="link-modal-description"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#E8E4DF]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#4A3B32]/10 flex items-center justify-center">
              <Link2 className="w-5 h-5 text-[#4A3B32]" />
            </div>
            <div>
              <h2 id="link-modal-title" className="font-heading text-lg text-[#2C2420]">
                {isEditing ? 'Edit Link' : 'Add New Link'}
              </h2>
              <p id="link-modal-description" className="text-sm text-[#5D524D]">
                {isEditing ? 'Update link details and verification status.' : 'Add a new link to an external source system.'}
              </p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            className="hover:bg-[#F0EBE6]"
            data-testid="close-link-modal-btn"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[#2C2420] mb-1">
              Title *
            </label>
            <Input
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter link title"
              className={errors.title ? 'border-red-500' : ''}
              data-testid="link-title-input"
            />
            {errors.title && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {errors.title}
              </p>
            )}
          </div>

          {/* Source System & Category */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Source System *
              </label>
              <Select value={formData.sourceSystem} onValueChange={(v) => handleChange('sourceSystem', v)}>
                <SelectTrigger className={errors.sourceSystem ? 'border-red-500' : ''} data-testid="link-source-select">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_SYSTEMS.map(source => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.sourceSystem && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {errors.sourceSystem}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Category *
              </label>
              <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                <SelectTrigger className={errors.category ? 'border-red-500' : ''} data-testid="link-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.category && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {errors.category}
                </p>
              )}
            </div>
          </div>

          {/* Owner & Review Cadence */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Owner *
              </label>
              <Input
                value={formData.owner}
                onChange={(e) => handleChange('owner', e.target.value)}
                placeholder="Owner name"
                className={errors.owner ? 'border-red-500' : ''}
                data-testid="link-owner-input"
              />
              {errors.owner && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {errors.owner}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Review Cadence
              </label>
              <Select value={formData.reviewCadence} onValueChange={(v) => handleChange('reviewCadence', v)}>
                <SelectTrigger data-testid="link-cadence-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REVIEW_CADENCES.map(cadence => (
                    <SelectItem key={cadence} value={cadence}>{cadence}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Status & Last Verified */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Status
              </label>
              <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                <SelectTrigger data-testid="link-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Last Verified
              </label>
              <Input
                type="date"
                value={formData.lastVerified}
                onChange={(e) => handleChange('lastVerified', e.target.value)}
                data-testid="link-verified-input"
              />
            </div>
          </div>

          {/* Linked Entity */}
          <div>
            <label className="block text-sm font-medium text-[#2C2420] mb-1">
              Linked Entity
            </label>
            <Select value={formData.linkedEntityId || "none"} onValueChange={(v) => handleChange('linkedEntityId', v === "none" ? "" : v)}>
              <SelectTrigger data-testid="link-entity-select">
                <SelectValue placeholder="Select an entity (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {entities?.map(entity => (
                  <SelectItem key={entity.id} value={entity.id}>
                    {entity.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reason (if needs review) */}
          {formData.status === 'Needs review' && (
            <div>
              <label className="block text-sm font-medium text-[#2C2420] mb-1">
                Reason for Review
              </label>
              <Input
                value={formData.reason}
                onChange={(e) => handleChange('reason', e.target.value)}
                placeholder="Why does this need review?"
                data-testid="link-reason-input"
              />
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-[#E8E4DF] bg-[#F9F7F5]">
          <Button variant="outline" onClick={onClose} className="caffeine-btn-secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} className="caffeine-btn" data-testid="save-link-btn">
            {isEditing ? 'Save Changes' : 'Add Link'}
          </Button>
        </div>
      </div>
    </div>
  );
}
